import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { serviceOptions } from "@/data/services";
import { site } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  type_demande: z.enum(["Devis commercial", "Recherche de fournisseur", "Partenariat", "Autre"], { errorMap: () => ({ message: "Veuillez choisir un type de demande." }) }),
  service: z.string().min(1, "Veuillez sélectionner un service."),
  societe: z.string().trim().min(2, "Le nom de la société est obligatoire."),
  siren: z.string().trim().refine((v) => !v || /^\d{9}$|^\d{14}$/.test(v.replace(/\s/g, "")), "Le SIREN (9 chiffres) ou SIRET (14 chiffres) est invalide."),
  contact: z.string().trim().min(2, "Le nom et prénom du contact sont obligatoires."),
  fonction: z.string().trim(),
  email: z.string().trim().email("Veuillez saisir une adresse email professionnelle valide."),
  telephone: z.string().trim().min(6, "Le numéro de téléphone est obligatoire.").regex(/^[+\d\s().-]{6,30}$/, "Le numéro de téléphone est invalide."),
  pays: z.string().trim(),
  produits: z.string().trim().min(2, "Veuillez indiquer les produits ou catégories concernés."),
  quantites: z.string().trim(),
  delai: z.string().trim(),
  budget: z.string(),
  description: z.string().trim().min(20, "La description doit comporter au moins 20 caractères."),
  rgpd: z.literal(true, { errorMap: () => ({ message: "Vous devez accepter le traitement de vos données pour envoyer la demande." }) }),
  newsletter: z.boolean(),
  website: z.string().max(0),
});

const Field = ({ id, label, required, error, hint, children }) => (
  <div>
    <label htmlFor={id} className="label">
      {label} {required && <span className="text-gold" aria-hidden="true">*</span>}
    </label>
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
    {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs font-medium text-red-700" data-testid={`${id}-error`}>{error.message}</p>}
  </div>
);

export const DevisForm = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const startedAt = useRef(Date.now());
  const [sending, setSending] = useState(false);
  const preService = params.get("service") || "";

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      type_demande: "Devis commercial", service: preService, societe: "", siren: "", contact: "", fonction: "", email: "", telephone: "",
      pays: "France", produits: "", quantites: "", delai: "", budget: "À définir", description: "", rgpd: false, newsletter: false, website: "",
    },
  });

  useEffect(() => { if (preService) setValue("service", preService); }, [preService, setValue]);

  const onSubmit = async (values) => {
    setSending(true);
    try {
      await axios.post(`${API}/devis`, { ...values, started_at: startedAt.current });
      toast.success("Votre demande a bien été envoyée. Nous vous répondons sous 48 heures ouvrées.");
      navigate("/merci", { state: { kind: "devis", societe: values.societe } });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "L'envoi a échoué.";
      toast.error(`${msg} Vous pouvez nous joindre au ${site.phone} ou à ${site.email}.`, { duration: 9000 });
    } finally {
      setSending(false);
    }
  };

  const cls = (name) => `field ${errors[name] ? "field-error" : ""}`;
  const a11y = (name) => ({ "aria-invalid": !!errors[name], "aria-describedby": errors[name] ? `${name}-error` : undefined });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6" data-testid="devis-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="type_demande" label="Type de demande" required error={errors.type_demande}>
          <select id="type_demande" className={cls("type_demande")} {...register("type_demande")} {...a11y("type_demande")} data-testid="devis-type-select">
            {["Devis commercial", "Recherche de fournisseur", "Partenariat", "Autre"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field id="service" label="Service concerné" required error={errors.service}>
          <select id="service" className={cls("service")} {...register("service")} {...a11y("service")} data-testid="devis-service-select">
            <option value="">Sélectionnez un service</option>
            {serviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field id="societe" label="Société" required error={errors.societe}>
          <input id="societe" className={cls("societe")} autoComplete="organization" {...register("societe")} {...a11y("societe")} data-testid="devis-societe-input" />
        </Field>
        <Field id="siren" label="SIREN / SIRET" error={errors.siren} hint="9 ou 14 chiffres (facultatif)">
          <input id="siren" inputMode="numeric" className={cls("siren")} {...register("siren")} {...a11y("siren")} data-testid="devis-siren-input" />
        </Field>
        <Field id="contact" label="Nom et prénom du contact" required error={errors.contact}>
          <input id="contact" className={cls("contact")} autoComplete="name" {...register("contact")} {...a11y("contact")} data-testid="devis-contact-input" />
        </Field>
        <Field id="fonction" label="Fonction" error={errors.fonction}>
          <input id="fonction" className={cls("fonction")} autoComplete="organization-title" {...register("fonction")} data-testid="devis-fonction-input" />
        </Field>
        <Field id="email" label="Email professionnel" required error={errors.email}>
          <input id="email" type="email" className={cls("email")} autoComplete="email" {...register("email")} {...a11y("email")} data-testid="devis-email-input" />
        </Field>
        <Field id="telephone" label="Téléphone" required error={errors.telephone}>
          <input id="telephone" type="tel" className={cls("telephone")} autoComplete="tel" {...register("telephone")} {...a11y("telephone")} data-testid="devis-telephone-input" />
        </Field>
        <Field id="pays" label="Pays" error={errors.pays}>
          <input id="pays" className={cls("pays")} autoComplete="country-name" {...register("pays")} data-testid="devis-pays-input" />
        </Field>
        <Field id="budget" label="Budget indicatif" error={errors.budget}>
          <select id="budget" className={cls("budget")} {...register("budget")} data-testid="devis-budget-select">
            {["< 5 000 €", "5 000 – 20 000 €", "20 000 – 50 000 €", "> 50 000 €", "À définir"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
      </div>

      <Field id="produits" label="Produits ou catégories concernés" required error={errors.produits}>
        <textarea id="produits" rows={2} className={cls("produits")} {...register("produits")} {...a11y("produits")} data-testid="devis-produits-input" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="quantites" label="Quantités / volumes estimés" error={errors.quantites}>
          <input id="quantites" className={cls("quantites")} {...register("quantites")} data-testid="devis-quantites-input" />
        </Field>
        <Field id="delai" label="Délai souhaité" error={errors.delai} hint="Date ou fourchette (ex. sous 6 semaines)">
          <input id="delai" className={cls("delai")} {...register("delai")} data-testid="devis-delai-input" />
        </Field>
      </div>
      <Field id="description" label="Description détaillée du besoin" required error={errors.description} hint="Minimum 20 caractères">
        <textarea id="description" rows={5} className={cls("description")} {...register("description")} {...a11y("description")} data-testid="devis-description-input" />
      </Field>

      {/* Honeypot anti-spam : champ invisible, doit rester vide */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="website">Ne pas remplir</label>
        <input id="website" tabIndex={-1} autoComplete="off" {...register("website")} />
      </div>

      <div className="space-y-3">
        <label className="flex items-start gap-3 text-sm text-ink">
          <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-gold" {...register("rgpd")} aria-invalid={!!errors.rgpd} data-testid="devis-rgpd-checkbox" />
          <span>
            J'accepte que mes données soient traitées par {site.name} dans le cadre de ma demande de devis, conformément à la{" "}
            <Link to="/politique-de-confidentialite" className="text-slate underline decoration-gold/60 underline-offset-4 hover:text-gold">politique de confidentialité</Link>. <span className="text-gold" aria-hidden="true">*</span>
          </span>
        </label>
        {errors.rgpd && <p role="alert" className="text-xs font-medium text-red-700" data-testid="rgpd-error">{errors.rgpd.message}</p>}
        <label className="flex items-start gap-3 text-sm text-ink">
          <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-gold" {...register("newsletter")} data-testid="devis-newsletter-checkbox" />
          <span>Je souhaite recevoir des informations commerciales de {site.name}.</span>
        </label>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted">* Champs obligatoires. Les informations transmises sont confidentielles et ne sont en aucun cas cédées à des tiers.</p>
        <button type="submit" disabled={sending} className="btn-gold !px-8 !py-4 text-base disabled:cursor-not-allowed disabled:opacity-60" data-testid="devis-submit-btn">
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Envoyer ma demande
        </button>
      </div>
    </form>
  );
};
